import { useState } from 'react'
import type { ProjectContextTerm } from '@ai-meeting/shared'
import { PlusIcon, TrashIcon, XMarkIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'

type DraftRow = { source: string; target: string }

type Props = {
  open: boolean
  disabled?: boolean
  onClose: () => void
  onSubmit: (terms: ProjectContextTerm[]) => Promise<void>
}

function emptyRow(): DraftRow {
  return { source: '', target: '' }
}

/**
 * Multi-row modal to add declared ACTIVE dictionary terms in one save.
 */
export default function AddDictionaryTermsModal({
  open,
  disabled = false,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useUiStrings()
  const [rows, setRows] = useState<DraftRow[]>([emptyRow()])
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  const canSubmit = rows.some((row) => row.source.trim().length > 0)

  async function handleSubmit() {
    const next: ProjectContextTerm[] = rows
      .map((row) => ({
        source: row.source.trim(),
        target: row.target.trim() || undefined,
        targetLang: '*' as const,
        status: 'ACTIVE' as const,
      }))
      .filter((term) => term.source.length > 0)
      .map((term) =>
        term.target
          ? {
              source: term.source,
              target: term.target,
              targetLang: term.targetLang,
              status: term.status,
            }
          : {
              source: term.source,
              targetLang: term.targetLang,
              status: term.status,
            }
      )

    if (next.length === 0) return
    setSubmitting(true)
    try {
      await onSubmit(next)
      setRows([emptyRow()])
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-dictionary-terms-title"
      data-testid="add-dictionary-terms-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
    >
      <div className="flex w-full max-w-2xl flex-col rounded-2xl bg-[var(--bg-secondary)] shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-color)] px-5 py-4">
          <h2
            id="add-dictionary-terms-title"
            className="text-lg font-bold text-[var(--text-primary)]"
          >
            {t.projectDetailDictAddTermsTitle}
          </h2>
          <button
            type="button"
            aria-label={t.projectDetailDictCloseModal}
            disabled={disabled || submitting}
            onClick={onClose}
            className="rounded-lg p-1 text-[var(--text-tertiary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-5 py-4">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-[11px] font-medium text-[var(--text-tertiary)]">
            <span>
              {t.projectDetailDictTermSource} <span className="text-[var(--error)]">*</span>
            </span>
            <span>
              {t.projectDetailDictTermTarget} <span className="text-[var(--error)]">*</span>
            </span>
            <span className="w-8" />
          </div>
          {rows.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-[1fr_1fr_auto] items-center gap-2"
            >
              <input
                value={row.source}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, i) => (i === index ? { ...r, source: e.target.value } : r))
                  )
                }
                className="input-field h-9 rounded-lg px-3 text-xs"
                disabled={disabled || submitting}
              />
              <input
                value={row.target}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, i) => (i === index ? { ...r, target: e.target.value } : r))
                  )
                }
                className="input-field h-9 rounded-lg px-3 text-xs"
                disabled={disabled || submitting}
              />
              <button
                type="button"
                aria-label={t.projectDetailDictRemoveRow}
                disabled={disabled || submitting || rows.length <= 1}
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                className="flex h-9 w-8 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-950/30"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            disabled={disabled || submitting}
            onClick={() => setRows((prev) => [...prev, emptyRow()])}
            className="inline-flex w-fit items-center gap-1.5 text-xs font-semibold text-[var(--accent)]"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {t.projectDetailDictAddRow}
          </button>
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--border-color)] px-5 py-4">
          <button
            type="button"
            disabled={disabled || submitting}
            onClick={onClose}
            className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-xs font-semibold text-[var(--text-secondary)]"
          >
            {t.projectDetailContextCancel}
          </button>
          <button
            type="button"
            disabled={disabled || submitting || !canSubmit}
            onClick={() => void handleSubmit()}
            className="btn-primary rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-50"
          >
            {t.projectDetailDictAddTerm}
          </button>
        </div>
      </div>
    </div>
  )
}
